<!--
  MedicalConditionsEditor — the structured "any medical information?" form.

  Used by: the sign-up form, the trialist invitation sign-up, and the
  profile / settings edit modal (via MedicalInfoCard). Pure form: it holds no
  server logic, it just edits a value of shape

    { hasConditions: null | 1 | 0, conditions: [Condition], notes: "" }

  Condition = Medical.blankCondition() (see services/medical-service.js).

  Flow: Yes/No first. "No" collapses everything. "Yes" shows one condition
  card (name, type, severity, medication + dosage, carries it?, triggers,
  what to do) with "Add another condition", plus a free-text "anything else"
  box at the bottom for things that don't fit the structure.
-->
<template>
  <div class="mce">
    <div class="mce__gate">
      <p class="mce__question">{{ question }}</p>
      <div class="mce__choices" role="radiogroup">
        <label class="mce__choice" :class="{ 'is-active': value.hasConditions === 1 }">
          <input type="radio" :name="radioName" :value="1" :checked="value.hasConditions === 1"
            @change="setHas(1)" />
          <span>Yes</span>
        </label>
        <label class="mce__choice" :class="{ 'is-active': value.hasConditions === 0 }">
          <input type="radio" :name="radioName" :value="0" :checked="value.hasConditions === 0"
            @change="setHas(0)" />
          <span>No</span>
        </label>
      </div>
      <p v-if="value.hasConditions === 0" class="mce__hint mce__hint--ok">
        <i class="bi bi-check-circle"></i> No medical conditions declared. You can update this at any
        time from the profile.
      </p>
    </div>

    <div v-if="value.hasConditions === 1" class="mce__details">
      <div v-for="(c, idx) in value.conditions" :key="idx" class="mce__card">
        <div class="mce__card-head">
          <strong>Condition {{ idx + 1 }}</strong>
          <button v-if="value.conditions.length > 1" type="button" class="mce__remove" @click="removeCondition(idx)"
            aria-label="Remove this condition">
            <i class="bi bi-trash"></i> Remove
          </button>
        </div>

        <div class="mce__row">
          <div class="mce__field">
            <label :for="id('type', idx)">Type</label>
            <select :id="id('type', idx)" class="form__input form__select" :value="c.category"
              @change="setField(idx, 'category', $event.target.value)">
              <option v-for="cat in categories" :key="cat.value" :value="cat.value">{{ cat.label }}</option>
            </select>
          </div>
          <div class="mce__field mce__field--grow">
            <label :for="id('name', idx)">Condition <span class="mce__req">*</span></label>
            <input :id="id('name', idx)" class="form__input" type="text" :value="c.condition_name"
              :placeholder="namePlaceholder(c.category)"
              @input="setField(idx, 'condition_name', $event.target.value)" />
          </div>
          <div class="mce__field">
            <label :for="id('sev', idx)">Severity</label>
            <select :id="id('sev', idx)" class="form__input form__select" :value="c.severity || ''"
              @change="setField(idx, 'severity', $event.target.value)">
              <option value="">Not sure</option>
              <option v-for="s in severities" :key="s.value" :value="s.value">{{ s.label }}</option>
            </select>
          </div>
        </div>

        <div class="mce__row">
          <div class="mce__field mce__field--grow">
            <label :for="id('med', idx)">Medication</label>
            <input :id="id('med', idx)" class="form__input" type="text" :value="c.medication"
              placeholder="e.g. Salbutamol (blue) inhaler, EpiPen 0.3mg, insulin"
              @input="setField(idx, 'medication', $event.target.value)" />
          </div>
          <div class="mce__field mce__field--grow">
            <label :for="id('dose', idx)">When / how much</label>
            <input :id="id('dose', idx)" class="form__input" type="text" :value="c.dosage_instructions"
              placeholder="e.g. 2 puffs before exercise and as needed"
              @input="setField(idx, 'dosage_instructions', $event.target.value)" />
          </div>
        </div>

        <div v-if="c.medication" class="mce__row">
          <div class="mce__field">
            <label>Does the player carry this medication to training and games?</label>
            <div class="mce__choices mce__choices--sm">
              <label class="mce__choice" :class="{ 'is-active': c.carries_medication === 1 }">
                <input type="radio" :name="id('carry', idx)" :checked="c.carries_medication === 1"
                  @change="setField(idx, 'carries_medication', 1)" />
                <span>Yes</span>
              </label>
              <label class="mce__choice" :class="{ 'is-active': c.carries_medication === 0 }">
                <input type="radio" :name="id('carry', idx)" :checked="c.carries_medication === 0"
                  @change="setField(idx, 'carries_medication', 0)" />
                <span>No</span>
              </label>
            </div>
          </div>
        </div>

        <div class="mce__row">
          <div class="mce__field mce__field--grow">
            <label :for="id('trig', idx)">Known triggers</label>
            <input :id="id('trig', idx)" class="form__input" type="text" :value="c.triggers"
              placeholder="e.g. cold weather, pollen, nuts, hard running"
              @input="setField(idx, 'triggers', $event.target.value)" />
          </div>
        </div>

        <div class="mce__row">
          <div class="mce__field mce__field--grow">
            <label :for="id('act', idx)">What should coaches do if it happens?</label>
            <textarea :id="id('act', idx)" class="form__input form__textarea" rows="2" :value="c.emergency_action"
              placeholder="e.g. Sit them down, give 4 puffs of the inhaler, call parent. If no improvement after 10 minutes call 999."
              @input="setField(idx, 'emergency_action', $event.target.value)"></textarea>
          </div>
        </div>
      </div>

      <button type="button" class="mce__add" @click="addCondition">
        <i class="bi bi-plus-circle"></i> Add another condition
      </button>

      <div class="mce__field mce__notes">
        <label :for="id('notes', 'x')">Anything else staff should know?</label>
        <textarea :id="id('notes', 'x')" class="form__input form__textarea" rows="2" :value="value.notes"
          placeholder="Optional - anything that doesn't fit above"
          @input="setNotes($event.target.value)"></textarea>
      </div>
    </div>

    <p v-if="showPrivacyHint" class="mce__hint">
      <i class="bi bi-shield-lock"></i> Medical information is only visible to the player, their parent,
      and the club's coaches and admins.
    </p>
  </div>
</template>

<script>
import { Medical } from "@/services/medical-service.js";

let uidCounter = 0;

export default {
  name: "MedicalConditionsEditor",
  props: {
    modelValue: {
      type: Object,
      default: () => ({ hasConditions: null, conditions: [], notes: "" }),
    },
    question: {
      type: String,
      default:
        "Does the player have any medical conditions, allergies or take any medication we should know about?",
    },
    showPrivacyHint: { type: Boolean, default: true },
  },
  emits: ["update:modelValue"],
  data() {
    uidCounter += 1;
    return {
      uid: uidCounter,
      categories: Medical.CATEGORIES,
      severities: Medical.SEVERITIES,
    };
  },
  computed: {
    value() {
      const v = this.modelValue || {};
      return {
        hasConditions:
          v.hasConditions === 1 || v.hasConditions === "1" || v.hasConditions === true
            ? 1
            : v.hasConditions === 0 || v.hasConditions === "0" || v.hasConditions === false
              ? 0
              : null,
        conditions: Array.isArray(v.conditions) ? v.conditions : [],
        notes: v.notes || "",
      };
    },
    radioName() {
      return `mce-has-${this.uid}`;
    },
  },
  methods: {
    id(part, idx) {
      return `mce-${this.uid}-${part}-${idx}`;
    },
    emit(patch) {
      this.$emit("update:modelValue", { ...this.value, ...patch });
    },
    setHas(val) {
      if (val === 1) {
        const conditions = this.value.conditions.length
          ? this.value.conditions
          : [Medical.blankCondition()];
        this.emit({ hasConditions: 1, conditions });
      } else {
        // "No" clears the detail so a stale entry can't be saved by accident.
        this.emit({ hasConditions: 0, conditions: [], notes: "" });
      }
    },
    setField(idx, key, val) {
      const conditions = this.value.conditions.map((c, i) =>
        i === idx ? { ...c, [key]: val } : c,
      );
      this.emit({ conditions });
    },
    setNotes(val) {
      this.emit({ notes: val });
    },
    addCondition() {
      this.emit({ conditions: [...this.value.conditions, Medical.blankCondition()] });
    },
    removeCondition(idx) {
      this.emit({ conditions: this.value.conditions.filter((_, i) => i !== idx) });
    },
    namePlaceholder(category) {
      switch (category) {
        case "allergy":
          return "e.g. Peanut allergy";
        case "asthma":
          return "e.g. Exercise-induced asthma";
        case "diabetes":
          return "e.g. Type 1 diabetes";
        case "epilepsy":
          return "e.g. Epilepsy";
        case "heart":
          return "e.g. Heart murmur";
        case "adhd_asd":
          return "e.g. ADHD";
        case "dietary":
          return "e.g. Coeliac / gluten free";
        case "injury":
          return "e.g. Osgood-Schlatter (knee)";
        default:
          return "Name of the condition";
      }
    },
  },
};
</script>

<style scoped lang="scss">
.mce {
  text-align: left;

  &__question {
    font-weight: 600;
    margin: 0 0 0.5rem;
    color: #1f2937;
  }

  &__choices {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;

    &--sm .mce__choice {
      padding: 4px 14px;
      font-size: 0.85rem;
    }
  }

  &__choice {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 22px;
    border: 1px solid #d1d5db;
    border-radius: 999px;
    cursor: pointer;
    background: #fff;
    font-weight: 500;
    color: #374151;
    margin: 0;
    transition: all 0.15s;

    input {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }

    &.is-active {
      background: #1f2937;
      border-color: #1f2937;
      color: #fff;
    }

    &:hover:not(.is-active) {
      border-color: #9ca3af;
    }
  }

  &__details {
    margin-top: 1rem;
  }

  &__card {
    border: 1px solid #e5e7eb;
    border-left: 3px solid #ef4444;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 0.75rem;
    background: #fafafa;
  }

  &__card-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    color: #374151;
  }

  &__remove {
    border: none;
    background: none;
    color: #b91c1c;
    font-size: 0.85rem;
    cursor: pointer;
    padding: 2px 6px;

    &:hover {
      text-decoration: underline;
    }
  }

  &__row {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-bottom: 0.5rem;
  }

  &__field {
    display: flex;
    flex-direction: column;
    min-width: 160px;
    flex: 1 1 160px;

    &--grow {
      flex: 2 1 220px;
    }

    label {
      font-size: 0.8rem;
      font-weight: 600;
      color: #4b5563;
      margin-bottom: 3px;
    }

    .form__input,
    .form__select,
    .form__textarea {
      width: 100%;
      font-size: 0.9rem;
    }
  }

  &__req {
    color: #ef4444;
  }

  &__add {
    border: 1px dashed #9ca3af;
    background: #fff;
    color: #374151;
    border-radius: 8px;
    padding: 8px 14px;
    cursor: pointer;
    font-size: 0.9rem;
    width: 100%;
    margin-bottom: 0.75rem;

    &:hover {
      border-color: #1f2937;
      background: #f9fafb;
    }
  }

  &__hint {
    font-size: 0.8rem;
    color: #6b7280;
    margin: 0.5rem 0 0;

    &--ok {
      color: #047857;
    }
  }
}
</style>
